import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useToast } from '@/hooks/ui/use-toast'

import { AuthLayout } from '@/components/AuthLayout'
import { Button, Input } from '@/components/ui'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { authApi } from '@/lib/api'
import {
  passwordResetRequestSchema,
  passwordResetConfirmSchema,
  type PasswordResetRequestFormData,
  type PasswordResetConfirmFormData,
} from '@/lib/validations'

export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()

  const uid = searchParams.get('uid') || ''
  const token = searchParams.get('token') || ''
  const isConfirmMode = useMemo(() => Boolean(uid && token), [uid, token])

  const requestForm = useForm<PasswordResetRequestFormData>({
    resolver: zodResolver(passwordResetRequestSchema),
    defaultValues: { email: '' },
  })

  const confirmForm = useForm<PasswordResetConfirmFormData>({
    resolver: zodResolver(passwordResetConfirmSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  })

  const requestReset = useMutation({
    mutationFn: ({ email }: PasswordResetRequestFormData) =>
      authApi.requestPasswordReset({ email }),
    onSuccess: () => {
      toast({
        title: 'Success!',
        description: 'If that email exists, we just sent password reset instructions.',
      })
      requestForm.reset()
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.email?.[0] ||
        'Unable to send reset email. Please try again.'
      toast({
        variant: 'destructive',
        title: 'Error',
        description: message,
      })
    },
  })

  const confirmReset = useMutation({
    mutationFn: ({ newPassword, confirmPassword }: PasswordResetConfirmFormData) =>
      authApi.confirmPasswordReset({
        uid,
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }),
    onSuccess: () => {
      toast({
        title: 'Success!',
        description: 'Password updated. You can sign in with your new password now.',
      })
      navigate('/login', { replace: true })
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.token?.[0] ||
        'Unable to reset password. Please request a new link.'
      toast({
        variant: 'destructive',
        title: 'Error',
        description: message,
      })
    },
  })

  const onSubmitRequest = (data: PasswordResetRequestFormData) => {
    requestReset.mutate(data)
  }

  const onSubmitConfirm = (data: PasswordResetConfirmFormData) => {
    confirmReset.mutate(data)
  }

  if (isConfirmMode && (!uid || !token)) {
    return (
      <AuthLayout
        title="Reset password"
        subtitle="The reset link is missing some details"
        footer={{
          text: 'Remembered your password?',
          linkText: 'Sign in',
          linkTo: '/login',
        }}
      >
        <div className="space-y-4 text-sm text-muted-foreground">
          <p>The reset link you opened is incomplete. Please request a new password reset email.</p>
          <Link to="/reset-password" className="text-primary hover:underline">
            Request a new reset link
          </Link>
        </div>
      </AuthLayout>
    )
  }

  if (isConfirmMode) {
    return (
      <AuthLayout
        title="Choose a new password"
        subtitle="Enter and confirm your new password to finish resetting"
        footer={{
          text: 'All set?',
          linkText: 'Sign in',
          linkTo: '/login',
        }}
      >
        <Form {...confirmForm}>
          <form onSubmit={confirmForm.handleSubmit(onSubmitConfirm)} className="space-y-4">
            <FormField
              control={confirmForm.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="Enter a strong password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={confirmForm.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="Re-enter your password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full"
              disabled={confirmForm.formState.isSubmitting || confirmReset.isPending}
            >
              {confirmForm.formState.isSubmitting || confirmReset.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting password...
                </>
              ) : (
                'Reset password'
              )}
            </Button>
          </form>
        </Form>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter your email and we will send reset instructions"
      footer={{
        text: 'Remembered your password?',
        linkText: 'Sign in',
        linkTo: '/login',
      }}
    >
      <Form {...requestForm}>
        <form onSubmit={requestForm.handleSubmit(onSubmitRequest)} className="space-y-4">
          <FormField
            control={requestForm.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="you@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full"
            disabled={requestForm.formState.isSubmitting || requestReset.isPending}
          >
            {requestForm.formState.isSubmitting || requestReset.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending reset link...
              </>
            ) : (
              'Send reset link'
            )}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            We'll email you a secure link to reset your password. For security we only let you
            request a link every few minutes.
          </p>
        </form>
      </Form>
    </AuthLayout>
  )
}
